import { c as _c } from "react/compiler-runtime";
import { SegmentedControl } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import * as d3 from 'd3';
import { useCallback, useEffect, useRef, useState } from 'react';
import { AddSectionButton, AddSectionOverlay } from '@/components/block-primitives';
import useMobileLayout from '@/hooks/use-mobile-layout';
import type { DataSeries, DataValue, DataViewConfig, DataViewMode } from './artifact-block-types';
import { CHART_MAX_HEIGHT, CHART_MIN_HEIGHT, clampChartHeight, DEFAULT_CHART_HEIGHT, getDataPoint } from './artifact-block-types';
import EditableText from './editable-text';
import { useResizeDrag } from './use-resize-drag';

// Raw hex because the design system has no categorical data palette: its colour tokens are
// semantic (success/warning/error), which would read as state on a series that carries none.
const SERIES_COLORS = ['#246b5c', '#d16a4d', '#416788', '#9a6a17', '#7a5d8c'];
const HEIGHT_KEY_STEP = 16;
const viewOptions = [{
  value: 'line',
  label: 'Line'
}, {
  value: 'bar',
  label: 'Bar'
}, {
  value: 'table',
  label: 'Table'
}] satisfies {
  value: DataViewMode;
  label: string;
}[];
type ArtifactDataViewBlockProps = {
  config: DataViewConfig;
  editable?: boolean;
  onChange?: (next: DataViewConfig) => void;
};
const formatValue = (value: number, format: DataViewConfig['format'] = 'decimal', currencyCode = 'USD', locale = 'en-US') => {
  // decimal/percent are precision ratios, not grouped magnitudes, so they stay
  // locale-independent; integer and currency follow the locale's grouping and symbol rules.
  if (format === 'percent') return `${(value * 100).toFixed(1)}%`;
  if (format === 'decimal') return value.toFixed(3);
  try {
    if (format === 'currency') {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currencyCode,
        maximumFractionDigits: 0
      }).format(value);
    }
    return Math.round(value).toLocaleString(locale);
  } catch {
    // The schema validates currencyCode and locale, but formatValue also runs inside render
    // and d3 effects, where a RangeError from a bad code/tag would crash the page — degrade
    // to a locale-free plain number.
    return Math.round(value).toLocaleString();
  }
};
const formatDataValue = (dataValue: DataValue, format: DataViewConfig['format'], currencyCode?: string, locale?: string) => {
  const point = getDataPoint(dataValue);
  const interval = point.lower !== undefined && point.upper !== undefined ? ` (${formatValue(point.lower, format, currencyCode, locale)} - ${formatValue(point.upper, format, currencyCode, locale)})` : '';
  return `${formatValue(point.value, format, currencyCode, locale)}${interval}`;
};

// Bars encode magnitude, so their axis must include zero; a domain that floats above zero
// would make small differences look large. Line mode keeps a tight author-supplied domain.
const getDomain = (config: DataViewConfig, mode: DataViewMode): [number, number] => {
  const forceZero = ([min, max]: [number, number]): [number, number] => mode === 'bar' ? [Math.min(0, min), Math.max(0, max)] : [min, max];
  if (config.yDomain) return forceZero(config.yDomain);
  const values = config.series.flatMap(series => series.values.flatMap(value => {
    const point = getDataPoint(value);
    return [point.value, point.lower, point.upper].filter((candidate): candidate is number => candidate !== undefined);
  }));
  if (config.referenceLine) values.push(config.referenceLine.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  if (min === max) return forceZero([min - 1, max + 1]);
  const padding = (max - min) * 0.12;
  return forceZero([min - padding, max + padding]);
};
const shortLabel = (label: string) => label.length > 18 ? `${label.slice(0, 17)}...` : label;
type ChartProps = {
  config: DataViewConfig;
  mode: Exclude<DataViewMode, 'table'>;
  height: number;
};
type TooltipState = {
  x: number;
  y: number;
  label: string;
  rows: {
    name: string;
    color: string;
    text: string;
  }[];
};
type WhiskerArgs = {
  group: d3.Selection<SVGGElement, unknown, null, undefined>;
  cx: number;
  value: DataValue;
  yScale: d3.ScaleLinear<number, number>;
  color: string;
};

// Confidence-interval whisker; no-op when the value carries no [lower, upper].
const drawWhisker = ({
  group,
  cx,
  value,
  yScale,
  color
}: WhiskerArgs) => {
  const point = getDataPoint(value);
  if (point.lower === undefined || point.upper === undefined) return;
  const upperY = yScale(point.upper);
  const lowerY = yScale(point.lower);
  const cap = (y: number) => group.append('line').attr('x1', cx - 4).attr('x2', cx + 4).attr('y1', y).attr('y2', y);
  group.append('line').attr('x1', cx).attr('x2', cx).attr('y1', upperY).attr('y2', lowerY);
  cap(upperY);
  cap(lowerY);
  group.selectAll('line').attr('stroke', color).attr('stroke-width', 1.5);
};
const DataChart = t0 => {
  const $ = _c(22);
  const {
    config,
    mode,
    height
  } = t0;
  const isMobile = useMobileLayout();
  const svgRef = useRef(null);
  const [width, setWidth] = useState(0);
  const [tooltip, setTooltip] = useState(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = node => {
      if (!node) {
        return;
      }
      setWidth(node.clientWidth);
      const observer = new ResizeObserver(entries => {
        const measured = entries[0]?.contentRect.width;
        if (measured && measured > 0) {
          setWidth(measured);
        }
      });
      observer.observe(node);
      return () => observer.disconnect();
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const measuredRef = t1;
  let t2;
  let t3;
  if ($[1] !== config || $[2] !== height || $[3] !== isMobile || $[4] !== mode || $[5] !== width) {
    t2 = () => {
      if (!svgRef.current || !width) {
        return;
      }
      const svg = d3.select(svgRef.current);
      svg.selectAll("*").remove();
      const [domainMin, domainMax] = getDomain(config, mode);
      const y = d3.scaleLinear().domain([domainMin, domainMax]).range([1, 0]);
      if (!config.yDomain) {
        y.nice();
      }
      const yTicks = y.ticks(5);
      const tickChars = Math.max(...yTicks.map(tick => formatValue(tick, config.format, config.currencyCode, config.locale).length));
      const margin = {
        top: 24,
        right: 24,
        bottom: config.xLabel ? 56 : 40,
        left: Math.ceil(tickChars * 6.5) + 14 + (config.yLabel ? 16 : 0)
      };
      const innerWidth = width - margin.left - margin.right;
      const innerHeight = height - margin.top - margin.bottom;
      y.range([innerHeight, 0]);
      const g = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);
      const x = d3.scaleBand().domain(config.categories).range([0, innerWidth]).paddingInner(0.3).paddingOuter(0.15);
      const xInner = d3.scaleBand().domain(config.series.map(_temp)).range([0, x.bandwidth()]).padding(0.08);
      const center = category => (x(category) ?? 0) + x.bandwidth() / 2;
      g.append("g").selectAll("line").data(yTicks).join("line").attr("x1", 0).attr("x2", innerWidth).attr("y1", tick_0 => y(tick_0)).attr("y2", tick_1 => y(tick_1)).attr("stroke", "var(--border-color-tertiary)");
      g.append("g").selectAll("text").data(yTicks).join("text").attr("x", -10).attr("y", tick_2 => y(tick_2)).attr("text-anchor", "end").attr("dominant-baseline", "middle").attr("font-size", 11).attr("fill", "var(--text-color-tertiary)").text(tick_3 => formatValue(tick_3, config.format, config.currencyCode, config.locale));
      if (config.referenceLine && config.referenceLine.value >= domainMin && config.referenceLine.value <= domainMax) {
        const refY = y(config.referenceLine.value);
        g.append("line").attr("x1", 0).attr("x2", innerWidth).attr("y1", refY).attr("y2", refY).attr("stroke", "var(--text-color-tertiary)").attr("stroke-dasharray", "5 5").attr("stroke-width", 1.5);
        g.append("text").attr("x", innerWidth).attr("y", refY - 7).attr("text-anchor", "end").attr("font-size", 10).attr("fill", "var(--text-color-tertiary)").text(config.referenceLine.label);
      }
      config.series.forEach((series_0, seriesIndex) => {
        const color = SERIES_COLORS[seriesIndex % SERIES_COLORS.length];
        if (mode === "bar") {
          config.categories.forEach((category_0, categoryIndex) => {
            const point = getDataPoint(series_0.values[categoryIndex]);
            const barX = (x(category_0) ?? 0) + (xInner(series_0.name) ?? 0);
            const baseline = y(0);
            g.append("rect").attr("x", barX).attr("y", Math.min(y(point.value), baseline)).attr("width", xInner.bandwidth()).attr("height", Math.max(1, Math.abs(y(point.value) - baseline))).attr("rx", 2).attr("fill", color);
            drawWhisker({
              group: g.append("g"),
              cx: barX + xInner.bandwidth() / 2,
              value: series_0.values[categoryIndex],
              yScale: y,
              color
            });
          });
        } else {
          const line = d3.line().x(index => center(config.categories[index])).y(index_0 => y(getDataPoint(series_0.values[index_0]).value));
          g.append("path").datum(d3.range(config.categories.length)).attr("fill", "none").attr("stroke", color).attr("stroke-width", 2.5).attr("stroke-linejoin", "round").attr("d", line);
          config.categories.forEach((category_1, categoryIndex_0) => {
            const cx = center(category_1);
            drawWhisker({
              group: g.append("g"),
              cx,
              value: series_0.values[categoryIndex_0],
              yScale: y,
              color
            });
            g.append("circle").attr("cx", cx).attr("cy", y(getDataPoint(series_0.values[categoryIndex_0]).value)).attr("r", 4).attr("fill", "var(--background-color-primary)").attr("stroke", color).attr("stroke-width", 2.5);
          });
        }
      });
      const widestLabelPx = Math.max(0, ...config.categories.map(_temp2)) * 6.5 + 8;
      const labelStep = isMobile ? Math.max(1, Math.ceil(widestLabelPx / x.step())) : 1;
      g.append("g").selectAll("text").data(config.categories.filter((_, index_1) => index_1 % labelStep === 0)).join("text").attr("x", category_3 => center(category_3)).attr("y", innerHeight + 20).attr("text-anchor", "middle").attr("font-size", 11).attr("fill", "var(--text-color-tertiary)").text(_temp3);
      if (config.xLabel) {
        g.append("text").attr("x", innerWidth / 2).attr("y", innerHeight + margin.bottom - 8).attr("text-anchor", "middle").attr("font-size", 11).attr("fill", "var(--text-color-tertiary)").text(config.xLabel);
      }
      if (config.yLabel) {
        g.append("text").attr("transform", `translate(${-margin.left + 14},${innerHeight / 2}) rotate(-90)`).attr("text-anchor", "middle").attr("font-size", 11).attr("fill", "var(--text-color-tertiary)").text(config.yLabel);
      }
      g.append("g").selectAll("rect").data(config.categories).join("rect").attr("x", category_5 => x(category_5) ?? 0).attr("y", 0).attr("width", x.bandwidth()).attr("height", innerHeight).attr("fill", "transparent").attr("cursor", "pointer").on("mousemove", (event, category_6) => {
        const bounds = svgRef.current?.getBoundingClientRect();
        if (!bounds) {
          return;
        }
        const categoryIndex_1 = config.categories.indexOf(category_6);
        setTooltip({
          x: event.clientX - bounds.left,
          y: event.clientY - bounds.top,
          label: category_6,
          rows: config.series.map((series_1, seriesIndex_0) => ({
            name: series_1.name,
            color: SERIES_COLORS[seriesIndex_0 % SERIES_COLORS.length],
            text: formatDataValue(series_1.values[categoryIndex_1], config.format, config.currencyCode, config.locale)
          }))
        });
      }).on("mouseleave", () => setTooltip(null));
      return () => {
        svg.selectAll("*").remove();
      };
    };
    t3 = [config, mode, width, height, isMobile];
    $[1] = config;
    $[2] = height;
    $[3] = isMobile;
    $[4] = mode;
    $[5] = width;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  useEffect(t2, t3);
  const t4 = width === 0;
  const t5 = `${config.title} ${mode} chart`;
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      display: "block",
      width: "100%"
    };
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== config.title) {
    t7 = <title>{config.title}</title>;
    $[9] = config.title;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== height || $[12] !== t5 || $[13] !== t7) {
    t8 = <svg aria-label={t5} height={height} ref={svgRef} role="img" style={t6}>{t7}</svg>;
    $[11] = height;
    $[12] = t5;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== tooltip || $[16] !== width) {
    t9 = tooltip && <div className="pointer-events-none absolute z-10 min-w-36 rounded-md px-3 py-2 shadow-md" style={{
      left: Math.min(tooltip.x + 12, width - 160),
      top: tooltip.y - 16,
      backgroundColor: "var(--text-color-primary)"
    }}><div className="mb-1 text-caption-3" style={{
        color: "var(--background-color-primary)"
      }}>{tooltip.label}</div>{tooltip.rows.map(_temp4)}</div>;
    $[15] = tooltip;
    $[16] = width;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t4 || $[19] !== t8 || $[20] !== t9) {
    t10 = <div className="px-3 pb-4 pt-2 sm:px-5"><div aria-busy={t4} className="relative" ref={measuredRef}>{t8}{t9}</div></div>;
    $[18] = t4;
    $[19] = t8;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  return t10;
};
const DataTable = t0 => {
  const $ = _c(23);
  const {
    config
  } = t0;
  const t1 = config.xLabel ?? "Group";
  let t2;
  if ($[0] !== t1) {
    t2 = <th className="border-b border-tertiary px-4 md:px-6 py-3" scope="col">{t1}</th>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== config.series) {
    t3 = config.series.map(_temp5);
    $[2] = config.series;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = <thead className="bg-secondary text-caption-3 uppercase text-tertiary"><tr>{t2}{t3}</tr></thead>;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== config.categories || $[8] !== config.currencyCode || $[9] !== config.format || $[10] !== config.locale || $[11] !== config.series) {
    let t6;
    if ($[13] !== config.currencyCode || $[14] !== config.format || $[15] !== config.locale || $[16] !== config.series) {
      t6 = (category, categoryIndex) => <tr className="border-b border-tertiary last:border-b-0" key={category}><th className="px-4 md:px-6 py-3 text-label-2 text-primary" scope="row">{category}</th>{config.series.map(series_0 => <td className="whitespace-nowrap px-4 md:px-6 py-3 text-right text-snippet-3 text-secondary" key={series_0.name}>{formatDataValue(series_0.values[categoryIndex], config.format, config.currencyCode, config.locale)}</td>)}</tr>;
      $[13] = config.currencyCode;
      $[14] = config.format;
      $[15] = config.locale;
      $[16] = config.series;
      $[17] = t6;
    } else {
      t6 = $[17];
    }
    t5 = config.categories.map(t6);
    $[7] = config.categories;
    $[8] = config.currencyCode;
    $[9] = config.format;
    $[10] = config.locale;
    $[11] = config.series;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[18] !== t5) {
    t6 = <tbody>{t5}</tbody>;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== t4 || $[21] !== t6) {
    t7 = <div className="overflow-x-auto"><table className="w-full border-collapse text-left text-body-2">{t4}{t6}</table></div>;
    $[20] = t4;
    $[21] = t6;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  return t7;
};
const ChartLegend = t0 => {
  const $ = _c(4);
  const {
    series
  } = t0;
  let t1;
  if ($[0] !== series) {
    t1 = series.map(_temp6);
    $[0] = series;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-tertiary px-4 md:px-6 py-3">{t1}</div>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
};
const ArtifactDataViewBlock = t0 => {
  const $ = _c(66);
  const {
    config,
    editable: t1,
    onChange
  } = t0;
  const editable = t1 === undefined ? false : t1;
  const [view, setView] = useState(config.defaultView ?? "line");
  const [addNote, setAddNote] = useState(false);
  const [draftHeight, setDraftHeight] = useState(null);
  let t2;
  if ($[0] !== config || $[1] !== onChange) {
    t2 = next => onChange?.({
      ...config,
      ...next
    });
    $[0] = config;
    $[1] = onChange;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const patch = t2;
  const showNote = !!config.note || addNote;
  const committedHeight = config.height ?? DEFAULT_CHART_HEIGHT;
  const chartHeight = draftHeight ?? committedHeight;
  const startY = useRef(0);
  const latestHeight = useRef(committedHeight);
  let t3;
  let t4;
  if ($[3] !== committedHeight) {
    t3 = event => {
      startY.current = event.clientY;
      latestHeight.current = committedHeight;
    };
    t4 = move => {
      latestHeight.current = clampChartHeight(committedHeight + (move.clientY - startY.current));
      setDraftHeight(latestHeight.current);
    };
    $[3] = committedHeight;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  let t5;
  if ($[6] !== committedHeight || $[7] !== patch) {
    t5 = () => {
      setDraftHeight(null);
      if (latestHeight.current !== committedHeight) {
        patch({
          height: latestHeight.current
        });
      }
    };
    $[6] = committedHeight;
    $[7] = patch;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t3 || $[10] !== t4 || $[11] !== t5) {
    t6 = {
      onStart: t3,
      onMove: t4,
      onEnd: t5
    };
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const {
    onPointerDown: handleResizeStart
  } = useResizeDrag(t6);
  let t7;
  if ($[13] !== committedHeight || $[14] !== patch) {
    t7 = event_0 => {
      const delta = event_0.key === "ArrowUp" ? -HEIGHT_KEY_STEP : event_0.key === "ArrowDown" ? HEIGHT_KEY_STEP : 0;
      if (!delta) {
        return;
      }
      event_0.preventDefault();
      const next_0 = clampChartHeight(committedHeight + delta);
      if (next_0 !== committedHeight) {
        patch({
          height: next_0
        });
      }
    };
    $[13] = committedHeight;
    $[14] = patch;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const handleResizeKeyDown = t7;
  let t8;
  if ($[16] !== editable || $[17] !== showNote) {
    t8 = editable && !showNote && <AddSectionOverlay position="bottom"><AddSectionButton label="Add a note" onClick={() => setAddNote(true)} /></AddSectionOverlay>;
    $[16] = editable;
    $[17] = showNote;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== patch) {
    t9 = title => patch({
      title
    });
    $[19] = patch;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== config.title || $[22] !== editable || $[23] !== t9) {
    t10 = <EditableText as="h3" className="text-label-2 text-primary" editable={editable} label="Chart title" onSubmit={t9} value={config.title} />;
    $[21] = config.title;
    $[22] = editable;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== config.description || $[26] !== editable || $[27] !== patch) {
    t11 = (editable || config.description) && <EditableText as="div" className="mt-1 max-w-2xl text-body-2 text-secondary" editable={editable} label="Chart description" multiline={true} onSubmit={description => patch({
      description
    })} optional={true} placeholder="Add a description" value={config.description ?? ""} />;
    $[25] = config.description;
    $[26] = editable;
    $[27] = patch;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== t10 || $[30] !== t11) {
    t12 = <div className="min-w-0">{t10}{t11}</div>;
    $[29] = t10;
    $[30] = t11;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== view) {
    t13 = <div className="shrink-0 self-start" contentEditable={false}><SegmentedControl aria-label="Visualization" onChange={setView} options={viewOptions} size="compact" value={view} /></div>;
    $[32] = view;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== t12 || $[35] !== t13) {
    t14 = <div className="flex flex-col gap-4 px-4 py-4 md:px-6 md:py-5">{t12}{t13}</div>;
    $[34] = t12;
    $[35] = t13;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== chartHeight || $[38] !== config || $[39] !== view) {
    t15 = view === "table" ? <DataTable config={config} /> : <DataChart config={config} mode={view} height={chartHeight} />;
    $[37] = chartHeight;
    $[38] = config;
    $[39] = view;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[41] !== chartHeight || $[42] !== editable || $[43] !== handleResizeKeyDown || $[44] !== handleResizeStart || $[45] !== view) {
    t16 = editable && view !== "table" && <div role="separator" aria-label="Resize chart height" aria-orientation="horizontal" aria-valuemin={CHART_MIN_HEIGHT} aria-valuemax={CHART_MAX_HEIGHT} aria-valuenow={chartHeight} tabIndex={0} data-resize-handle={true} contentEditable={false} className={cx("mx-auto my-1 h-1.5 w-16 cursor-ns-resize rounded-full bg-transparent outline-none transition", "opacity-0 group-hover:bg-tertiary group-hover:opacity-100 focus-visible:bg-tertiary focus-visible:opacity-100", "in-data-force-affordance:bg-tertiary in-data-force-affordance:opacity-100")} onPointerDown={handleResizeStart} onKeyDown={handleResizeKeyDown} />;
    $[41] = chartHeight;
    $[42] = editable;
    $[43] = handleResizeKeyDown;
    $[44] = handleResizeStart;
    $[45] = view;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  let t17;
  if ($[47] !== config.series || $[48] !== view) {
    t17 = view !== "table" && <ChartLegend series={config.series} />;
    $[47] = config.series;
    $[48] = view;
    $[49] = t17;
  } else {
    t17 = $[49];
  }
  let t18;
  if ($[50] !== addNote || $[51] !== config.note || $[52] !== editable || $[53] !== patch || $[54] !== showNote) {
    t18 = showNote && <div className="border-t border-tertiary bg-secondary px-4 md:px-6 py-3 text-caption-4 text-tertiary"><EditableText autoFocus={addNote} block={true} editable={editable} label="Chart note" onSubmit={note => patch({
        note
      })} onEmptyBlur={() => setAddNote(false)} optional={true} placeholder="Add a note" value={config.note ?? ""} /></div>;
    $[50] = addNote;
    $[51] = config.note;
    $[52] = editable;
    $[53] = patch;
    $[54] = showNote;
    $[55] = t18;
  } else {
    t18 = $[55];
  }
  let t19;
  if ($[56] !== config.title || $[57] !== t14 || $[58] !== t15 || $[59] !== t16 || $[60] !== t17 || $[61] !== t18) {
    t19 = <section aria-label={config.title} className="overflow-hidden rounded-lg border border-tertiary bg-primary">{t14}{t15}{t16}{t17}{t18}</section>;
    $[56] = config.title;
    $[57] = t14;
    $[58] = t15;
    $[59] = t16;
    $[60] = t17;
    $[61] = t18;
    $[62] = t19;
  } else {
    t19 = $[62];
  }
  let t20;
  if ($[63] !== t19 || $[64] !== t8) {
    t20 = <div className="group relative">{t8}{t19}</div>;
    $[63] = t19;
    $[64] = t8;
    $[65] = t20;
  } else {
    t20 = $[65];
  }
  return t20;
};
export default ArtifactDataViewBlock;
function _temp(series) {
  return series.name;
}
function _temp2(category_2) {
  return shortLabel(category_2).length;
}
function _temp3(category_4) {
  return shortLabel(category_4);
}
function _temp4(row) {
  return <div className="flex items-center gap-1.5 text-caption-4 text-quaternary" key={row.name}><span aria-hidden="true" className="h-2 w-2 flex-none rounded-full" style={{
      backgroundColor: row.color
    }} />{row.name}: {row.text}</div>;
}
function _temp5(series) {
  return <th className="border-b border-tertiary px-4 md:px-6 py-3 text-right" key={series.name} scope="col">{series.name}</th>;
}
function _temp6(item, index) {
  return <div className="flex items-center gap-2 text-caption-4 text-secondary" key={item.name}><span aria-hidden="true" className="h-2.5 w-2.5 rounded-sm" style={{
      backgroundColor: SERIES_COLORS[index % SERIES_COLORS.length]
    }} />{item.name}</div>;
}